import { useEffect, useState } from "react";

// MUI
import { Stack, SvgIcon } from "@mui/material";

// Interfaces
import { IChargebackReferenceProps, IChargebackReferenceType } from "./interfaces";

// Helpers
import { isSimilarObject } from "@/Common/helpers/Verifications/objects";

// Layouts
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import SimpleDoubleChecking from "@/Layouts/components/Buttons/SimpleDoubleChecking";

// Common Components
import InfoSubTitle from "../../../common/InfoSubTitle";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ReactComponent as TrashIcon } from "@/assets/icons/actions/trash.svg";

const getSeparatorClasses = (index: number) => (!index ? "!pt-[40px]" : "");

// Constants
const references = [
	{
		label: "Customer Pay Parts",
		description: "Customer Pay (P)",
		repairOrderRoute: "customerPay.parts.amount",
	},
	{
		label: "Customer Pay Labor",
		description: "Customer Pay (L)",
		repairOrderRoute: "customerPay.labor.amount",
	},
	{
		label: "Warranty Parts",
		description: "Warranty (P)",
		repairOrderRoute: "warranty.parts.amount",
	},
	{
		label: "Warranty Labor",
		description: "Warranty (L)",
		repairOrderRoute: "warranty.labor.amount",
	},
	{
		label: "Internal Parts",
		description: "Internal (P)",
		repairOrderRoute: "internal.parts.amount",
	},
	{
		label: "Internal Labor",
		description: "Internal (L)",
		repairOrderRoute: "internal.labor.amount",
	},
];

const getReference = (label: string) => references.find((item) => item.label === label);
const mapAccountNumbers = (accounts: string[]) => accounts.map((value: string) => ({ label: value }));
const mapSelectedAccounts = (accounts: { label: string }[]) => accounts.map((item) => item.label);
const mapAccountReference = (reference: Partial<IChargebackReferenceType>) => ({
	accountNumbers: reference?.accountNumbers ?? [],
	description: reference?.description ?? "",
	label: reference?.label ?? "",
	repairOrderRoute: reference?.repairOrderRoute ?? "",
});

export default function ChargebackReference(props: Readonly<IChargebackReferenceProps>) {
	const {
		index = 0,
		label,
		accountNumbers = [],
		disabledDelete = false,
		readOnly = false,
		saving = false,
		onActionUpdate = () => {},
		onActionDelete = () => {},
	} = props;
	const [accounts, setAccounts] = useState<string[]>([]);
	const [reference, setReference] = useState<{ label: string; repairOrderRoute: string; description: string } | null>(null);

	const update = (data: Partial<IChargebackReferenceType>) => {
		onActionUpdate(mapAccountReference(data));
	};

	const handleSelectAccount = (selected: { label: string }[]) => {
		const updated = [...mapSelectedAccounts(selected)];
		if (!isSimilarObject({ accounts: updated }, { accounts: accountNumbers })) {
			setAccounts(updated);
			update({ accountNumbers: updated, ...reference });
		}
	};

	const handleSelectReference = (selected: { label: string }) => {
		const updated = getReference(selected?.label);
		if (updated && updated?.label !== reference?.label) {
			setReference(updated);
			update({ ...updated, accountNumbers: accounts });
		}
	};

	useEffect(() => {
		const currentReference = getReference(label);
		setAccounts([...accountNumbers]);
		setReference(currentReference ?? null);
		return () => {
			setAccounts([]);
			setReference(null);
		};
	}, [accountNumbers, label]);

	return (
		<Stack direction={"row"} spacing={1.5}>
			<Stack className="!min-w-[240px]">
				{!index && <InfoSubTitle title="Account Number(s)" className="!mb-[12px]" />}
				<Stack direction={"row"} position={"relative"} width={"240px"}>
					<SmartSelector
						addable
						multiple
						showBy={"label"}
						selectBy={"label"}
						options={mapAccountNumbers(accounts)}
						selected={mapAccountNumbers(accounts)}
						inputProps={{
							placeholder: "Account Number(s)",
							classes: `[&_.MuiInputBase-root]:!py-[3px]`,
						}}
						renderTagsCustomProps={{
							classes: "!font-sans",
						}}
						disabled={readOnly}
						error={!accounts?.length}
						clearable={Boolean(accounts?.length)}
						loading={saving}
						sx={{ minWidth: "100%" }}
						onChangeAction={(selected) => handleSelectAccount(selected as { label: string }[])}
					/>
				</Stack>
			</Stack>
			<Stack>
				<Stack position={"relative"} justifyContent={"center"} alignItems={"center"} className={getSeparatorClasses(index)}>
					<InfoSubTitle title="-" />
				</Stack>
			</Stack>
			<Stack className="!min-w-[240px]">
				{!index && <InfoSubTitle title="Reserve Difference Source" className="!mb-[12px]" />}
				<Stack position={"relative"} width={"240px"}>
					<SmartSelector
						showBy={"label"}
						selectBy={"label"}
						options={references}
						selected={reference}
						inputProps={{
							placeholder: "Reserve Difference Source",
						}}
						disabled={readOnly}
						error={!reference}
						loading={saving}
						sx={{ minWidth: "100%" }}
						onChangeAction={(selected) => handleSelectReference(selected as { label: string })}
					/>
				</Stack>
			</Stack>
			<Stack alignContent={"center"} justifyContent={"center"} ml={"5px !important"}>
				<SimpleDoubleChecking
					className={"btn-delete"}
					color={"var(--gray-blue-color)"}
					confirmationButtonColor={"var(--gray-blue-color)"}
					icon={<CloseRoundedIcon fontSize="small" />}
					iconHover={<CloseRoundedIcon fontSize="small" />}
					confirmationIcon={
						<SvgIcon
							viewBox={"0 0 14 14"}
							className="[&>path]:fill-gray-blue !text-gray-blue !text-[18px] !ml-[2px]"
							component={TrashIcon}
						/>
					}
					sx={{ marginTop: index ? "-3px" : "34px" }}
					disabled={saving || readOnly || disabledDelete}
					onDeleteAction={onActionDelete}
				/>
			</Stack>
		</Stack>
	);
}
